import {
  AddToWishlistContainer,
  BasicDetailsComponent,
  ConditionalPriceRangeComponent,
  DetailsType,
  PriceType,
  ProductIdAndSizeComponent,
  IdAndSizeType,
  PVBVComponent,
  PVBVType,
  StockStatusComponent,
  ConditionalRetailPriceRangeComponent,
  StockStatusDrawer,
  StockStatusComponentType,
} from '@commerce-product-webcomponents/ui';
import { PromotionDetails } from '@commerce-promotion-webcomponents/ui';
import { useTranslation } from '@nextgen-web-framework/all';
import { FC } from 'react';
import { InstallmentDetails, SharebarContainer } from '../../Molecules';
import styles from './ProductSpecs.module.scss';
import { ProductSpecsPropsTypes } from './types/propsTypes';

const ProductSpecs: FC<ProductSpecsPropsTypes> = ({
  brandName,
  productName,
  productId,
  productSize,
  productPrice,
  currency,
  hasVariant = false,
  isBundle,
  minPrice,
  maxPrice,
  pointValue,
  businessVolume,
  derivedAvailabilityStatus,
  firstAvailableFutureDate,
  promotionData,
  productData,
  subscriptionPlanData,
  bundleStateToBeStored,
  showSubText = true,
  dateSubText,
  isCachedDataLoaded,
  showZeroAsFree,
  productRetailPrice,
  minRetailPrice,
  maxRetailPrice,
  isBundleConditionMatched,
}: ProductSpecsPropsTypes): JSX.Element => {
  const t = useTranslation();
  const { firedPromotions = [], potentialPromotions = [] } = promotionData ?? {};
  const { productFlags } = productData;

  return (
    <>
      <div className={styles['cpw-product-specs']}>
        <div className={styles['product-specs-top-section']}>
          <div className={styles['wishlist-sharebar-row']}>
            <AddToWishlistContainer
              bundleState={bundleStateToBeStored}
              isPDP={true}
              isPLPCard={false}
              productData={productData}
              isBundle={isBundle}
            />
            <SharebarContainer
              productData={productData}
              subscriptionPlanData={subscriptionPlanData}
              bundleStateToBeStored={bundleStateToBeStored}
              isBundleConditionMatched={isBundleConditionMatched}
            />
          </div>
          <div className={styles['product-specs-mandatory-details-section']}>
            <BasicDetailsComponent
              brandName={brandName}
              productName={productName}
              detailsType={DetailsType.PDP_DETAILS}
            />
            <ProductIdAndSizeComponent
              productId={`${t('product.composite.common.item')} ${productId}`}
              productSize={productSize}
              showProductId={true}
              idSizeType={IdAndSizeType.ID_SIZE_PRODUCT_SPEC}
            />
          </div>
        </div>
        <div className={styles['product-specs-middle-section']}>
          {isCachedDataLoaded && (
            <ConditionalPriceRangeComponent
              isBundle={isBundle}
              minPrice={minPrice}
              maxPrice={maxPrice}
              currency={currency}
              productPrice={productPrice}
              condionalPriceType={PriceType.PRICE_PDP}
              showZeroAsFree={showZeroAsFree}
            />
          )}
          <ConditionalRetailPriceRangeComponent
            isBundle={isBundle}
            minPrice={minRetailPrice as number}
            maxPrice={maxRetailPrice as number}
            currency={currency}
            productPrice={productRetailPrice as number}
            condionalPriceType={PriceType.PRICE_PDP}
            isRetailPrice={true}
          />
          <PVBVComponent
            pointValue={pointValue}
            businessVolume={businessVolume}
            pvbvType={PVBVType.PVBV_PRODUCT_SPEC}
          />
        </div>
        <div className={styles['product-specs-bottom-section']}>
          {(!!potentialPromotions?.length || !!firedPromotions?.length) && (
            <PromotionDetails firedPromotions={firedPromotions} potentialPromotions={potentialPromotions} />
          )}
          {productFlags?.isInstallmentPlanEligible && <InstallmentDetails productId={productId} isAlertLink />}
          {!isBundle && <StockStatusDrawer itemNumber={productId} />}
          {!hasVariant && derivedAvailabilityStatus && isCachedDataLoaded && (
            <StockStatusComponent
              derivedAvailabilityStatus={derivedAvailabilityStatus}
              firstAvailableFutureDate={firstAvailableFutureDate}
              showSubText={showSubText}
              dateSubText={dateSubText}
              stockStatusType={StockStatusComponentType.PRODUCT_SPEC}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default ProductSpecs;
export type { ProductSpecsPropsTypes };
